/**
 * nb-NO formatting — decimal comma throughout. Uses Intl.NumberFormat where
 * available, with a manual fallback because iOS 10 Safari's Intl can be flaky
 * (verify on the reference device; the fallback keeps the app from crashing).
 */

let nfCache: Map<string, Intl.NumberFormat> | null = null;

function intlFormat(value: number, minFrac: number, maxFrac: number): string | null {
  try {
    if (typeof Intl === 'undefined' || typeof Intl.NumberFormat !== 'function') return null;
    nfCache ??= new Map();
    const key = `${minFrac}:${maxFrac}`;
    let nf = nfCache.get(key);
    if (!nf) {
      nf = new Intl.NumberFormat('nb-NO', {
        minimumFractionDigits: minFrac,
        maximumFractionDigits: maxFrac,
      });
      nfCache.set(key, nf);
    }
    const out = nf.format(value);
    // Guard against engines that ignore the locale and emit a dot.
    return out.indexOf('.') === -1 ? out : null;
  } catch {
    return null;
  }
}

/** Manual nb-NO fallback: fixed decimals, comma separator, space thousands. */
function manualFormat(value: number, fractionDigits: number): string {
  const neg = value < 0;
  const fixed = Math.abs(value).toFixed(fractionDigits);
  const [intPart = '0', fracPart] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const body = fracPart ? `${grouped},${fracPart}` : grouped;
  return neg ? `−${body}` : body;
}

export function formatNumber(value: number, fractionDigits: number): string {
  return intlFormat(value, fractionDigits, fractionDigits) ?? manualFormat(value, fractionDigits);
}

/** Speed over ground: one decimal, jitter floor at 0.5 kn → "0,0". */
export function formatSog(sogKn: number | null): string {
  if (sogKn === null || Number.isNaN(sogKn)) return '—';
  const v = sogKn < 0.5 ? 0 : sogKn;
  return formatNumber(v, 1);
}

// --- unit-aware display (spec 07 ENHETER); storage stays kn/nm internally ---

export type SpeedUnit = 'kn' | 'kmh';
export type DistanceUnit = 'nm' | 'km';

const KMH_PER_KN = 1.852;
const KM_PER_NM = 1.852;

export const speedUnitLabel: Record<SpeedUnit, string> = { kn: 'kn', kmh: 'km/t' };
export const distanceUnitLabel: Record<DistanceUnit, string> = { nm: 'nm', km: 'km' };

/** Convert an internal kn speed to the chosen unit (value only). */
export function speedValue(sogKn: number, unit: SpeedUnit): number {
  const floored = sogKn < 0.5 ? 0 : sogKn;
  return unit === 'kmh' ? floored * KMH_PER_KN : floored;
}

/** SOG string with jitter floor, one decimal, unit-aware. */
export function formatSogUnit(sogKn: number | null, unit: SpeedUnit): string {
  if (sogKn === null || Number.isNaN(sogKn)) return '—';
  return formatNumber(speedValue(sogKn, unit), 1);
}

export function distanceValue(nm: number, unit: DistanceUnit): number {
  return unit === 'km' ? nm * KM_PER_NM : nm;
}

/** Distance with adaptive precision + unit suffix. */
export function formatDistanceUnit(nm: number, unit: DistanceUnit): string {
  const v = distanceValue(nm, unit);
  const digits = v < 10 ? 2 : 1;
  return `${formatNumber(v, digits)} ${distanceUnitLabel[unit]}`;
}

/** Course over ground: 3-digit degrees, "—" when meaningless (caller decides). */
export function formatCog(cogDeg: number | null): string {
  if (cogDeg === null || Number.isNaN(cogDeg)) return '—';
  const d = Math.round(((cogDeg % 360) + 360) % 360) % 360;
  return `${String(d).padStart(3, '0')}°`;
}

/** Distance in nautical miles, adaptive precision. */
export function formatNm(nm: number): string {
  const digits = nm < 10 ? 2 : 1;
  return `${formatNumber(nm, digits)} nm`;
}

/** Duration from minutes → "1 t 20 min" / "45 min". */
export function formatDuration(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 0) return '—';
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  return `${h} t ${String(m).padStart(2, '0')} min`;
}

/** Fuel litres. */
export function formatFuel(litres: number): string {
  return `${formatNumber(litres, 1)} l`;
}

/**
 * Parse a decimal the user typed, accepting both comma and dot (nb-NO users
 * type commas). Returns null for blank/invalid so the caller can keep the old
 * value (spec 07 numeric input rule).
 */
export function parseDecimal(input: string): number | null {
  const cleaned = input.trim().replace(',', '.');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * Degrees + decimal minutes, nb-NO hemisphere letters (N/S, Ø/V) — the label
 * for a go-to pin dropped by tapping the map, which has no place name
 * (spec 13). E.g. `formatLatLon(59.35, 5.128)` → `"59°21,0′N 05°07,7′Ø"`.
 */
function formatLatLonPart(value: number, digits: number, posLabel: string, negLabel: string): string {
  const label = value >= 0 ? posLabel : negLabel;
  const abs = Math.abs(value);
  let deg = Math.floor(abs);
  // Round to 1 decimal minute first so e.g. 59,999… doesn't display as a
  // nonsensical "60,0′" — carry into the next whole degree instead.
  let min = Math.round((abs - deg) * 60 * 10) / 10;
  if (min >= 60) {
    min = 0;
    deg += 1;
  }
  const minWhole = Math.floor(min);
  const minFrac = Math.round((min - minWhole) * 10);
  return `${String(deg).padStart(digits, '0')}°${String(minWhole).padStart(2, '0')},${minFrac}′${label}`;
}

export function formatLatLon(lat: number, lon: number): string {
  return `${formatLatLonPart(lat, 2, 'N', 'S')} ${formatLatLonPart(lon, 2, 'Ø', 'V')}`;
}

/** Clock time HH:MM in nb-NO 24h for an epoch-ms instant (ETA display). */
export function formatClockTime(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Clock time HH:MM:SS in nb-NO 24h — the AIS vessel sheet's "sist sett" stamp
 *  (spec 16), which needs second-level precision unlike the ETA/weather displays. */
export function formatClockTimeSec(ms: number): string {
  const d = new Date(ms);
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${formatClockTime(ms)}:${ss}`;
}
